// Member Status Enum
export enum MemberStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  PENDING = 'pending',
}

// Non-Member Status Enum
export enum NonMemberStatus {
  NON_MEMBER_ACTIVE = 'non_member_active',
  NON_MEMBER_INACTIVE = 'non_member_inactive',
  NON_MEMBER_BLOCKED = 'non_member_blocked',
}

// Guest Pass Status Enum
export enum GuestPassStatus {
  ISSUED = 'issued',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
  NO_SHOW = 'no_show',
}

// Payment Status Enum
export enum PaymentStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
  REFUNDED = 'refunded',
}

// Invoice Status Enum
export enum InvoiceStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  PAID = 'paid',
  OVERDUE = 'overdue',
  CANCELLED = 'cancelled',
}

// Payment Method Enum
export enum PaymentMethod {
  STRIPE = 'stripe',
  CASH = 'cash',
  CHECK = 'check',
  BANK_TRANSFER = 'bank_transfer',
}

// Membership Tier Interface
export interface MembershipTier {
  id: string;
  tier_name: string;
  monthly_fee: number;
  annual_fee: number;
  benefits?: string[];
  max_family_members: number;
  description: string;
  created_at: string;
  updated_at: string;
}

// Family Member Interface
export interface FamilyMember {
  id: string;
  member_id: string;
  first_name: string;
  last_name: string;
  relationship_type: string;
  date_of_birth: string;
  email?: string;
  phone?: string;
  created_at: string;
  updated_at: string;
}

// Member Role Enum
export enum MemberRole {
  MEMBER = 'member',
  ADMIN = 'admin',
  STAFF = 'staff',
  SUPERUSER = 'superuser',
}

// Member Type Enum
export enum MemberType {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  DEPENDENT = 'DEPENDENT',
}

// Member Interface
export interface Member {
  id: string;
  email: string;
  email_verified?: boolean;
  first_name: string;
  last_name: string;
  phone?: string;
  date_of_birth?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  membership_tier_id: string;
  membershipTier?: MembershipTier;
  member_number?: string;
  membership_status?: string;
  joined_date?: string;
  renewal_date?: string;
  profile_photo_url?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  role: string;
  is_active?: boolean;
  is_locked?: boolean;
  locked_at?: string;
  locked_reason?: string;
  failed_login_attempts?: number;
  member_type?: string; // PRIMARY, SECONDARY, DEPENDENT (or lowercase variants)
  primary_member_id?: string; // ID of the primary household member (for SECONDARY/DEPENDENT members)
  billing_responsible?: boolean;
  relationship_to_primary?: string;

  // Extended Contact (NEW)
  mobile_phone?: string;
  home_phone?: string;
  work_phone?: string;
  secondary_email?: string;
  preferred_contact_method?: string;

  // Demographics (NEW)
  gender?: string;
  preferred_pronouns?: string;
  occupation?: string;
  employer?: string;

  // Mailing Address (NEW)
  mailing_same_as_home?: boolean;
  mailing_address_line1?: string;
  mailing_address_line2?: string;
  mailing_city?: string;
  mailing_state?: string;
  mailing_postal_code?: string;
  mailing_country?: string;

  // Club Preferences (NEW)
  golf_handicap?: number;
  ghin_number?: string;
  activity_interests?: any;
  dietary_restrictions?: string;
  accessibility_needs?: string;
  preferred_tee_time?: string;

  // Emergency Contact Enhancements (NEW)
  emergency_contact_relationship?: string;
  emergency_contact_email?: string;
  medical_conditions?: string;
  allergies?: string;

  // Privacy & Communication (NEW - GDPR)
  newsletter_opt_in?: boolean;
  marketing_opt_in?: boolean;
  sms_opt_in?: boolean;
  directory_opt_in?: boolean;
  profile_visibility?: string;
  consent_date?: string;
  privacy_policy_accepted?: boolean;
  privacy_policy_accepted_date?: string;

  // Membership History (NEW)
  referral_source?: string;
  referred_by_member_id?: string;
  previous_membership_id?: string;
  membership_notes?: string;

  // Social (NEW)
  linkedin_url?: string;
  spouse_name?: string;
  anniversary_date?: string;

  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  created_at?: string;
  updated_at?: string;
}

// Non-Member Interface (extends Member with guest-specific fields)
export interface NonMember extends Member {
  // Guest Visit Tracking
  guest_visits_this_year?: number;
  guest_annual_limit?: number;
  guest_visits_reset_date?: string;

  // Sponsor Information
  default_sponsor_member_id?: string;

  // Acquisition & Conversion
  acquisition_source?: string;
  acquisition_date?: string;
  conversion_interest_level?: string;
  conversion_notes?: string;
  last_activity_date?: string;
  non_member_pricing_tier?: string;
}

// Guest Pass Interface
export interface GuestPass {
  id: string;
  guest_member_id: string;
  sponsor_member_id: string;
  pass_code: string;
  visit_date: string;
  status: GuestPassStatus | string;
  facilities_allowed?: string[];
  check_in_time?: string;
  check_out_time?: string;
  checked_in_by_staff_id?: string;
  guest_fee_amount?: number;
  fee_billed_to?: string;
  payment_id?: string;
  notes?: string;
  cancellation_reason?: string;
  created_at?: string;
  updated_at?: string;
  created_by_member_id?: string;
  // Relations
  guest?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    guest_visits_this_year?: number;
    remaining_visits?: number;
  };
  sponsor?: {
    id: string;
    first_name: string;
    last_name: string;
    member_number?: string;
  };
}

// Non-Member Registration Data
export interface NonMemberRegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  acquisition_source?: string;
}

// Benefit Interface
export interface Benefit {
  id: string;
  membershipTierId: string;
  benefitName: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

// Payment Interface
export interface Payment {
  id: string;
  memberId: string;
  member?: Member;
  amount: number;
  currency: string;
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  transactionId?: string;
  stripePaymentIntentId?: string;
  invoiceId?: string;
  description?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// Invoice Interface
export interface Invoice {
  id: string;
  invoiceNumber: string;
  memberId: string;
  member?: Member;
  amount: number;
  currency: string;
  status: InvoiceStatus;
  dueDate: string;
  paidDate?: string;
  description?: string;
  lineItems?: InvoiceLineItem[];
  createdAt: string;
  updatedAt: string;
}

// Invoice Line Item Interface
export interface InvoiceLineItem {
  id: string;
  invoiceId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  createdAt: string;
  updatedAt: string;
}

// Authentication Interfaces
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  date_of_birth?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  membership_tier_id: string;
}

export interface AuthResponse {
  accessToken: string;
  member: Member;
}

// API Response Interfaces
export interface ApiError {
  message: string;
  errors?: string[];
  statusCode?: number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Filter Interfaces
export interface MemberFilters extends PaginationParams {
  status?: MemberStatus;
  membership_tier_id?: string;
  search?: string;
  query?: string; // Backend search parameter
}

export interface PaymentFilters extends PaginationParams {
  memberId?: string;
  paymentStatus?: PaymentStatus;
  paymentMethod?: PaymentMethod;
  startDate?: string;
  endDate?: string;
}

export interface InvoiceFilters extends PaginationParams {
  memberId?: string;
  status?: InvoiceStatus;
  startDate?: string;
  endDate?: string;
}

// Update Profile Data Interface
export interface UpdateProfileData {
  first_name?: string;
  last_name?: string;
  phone?: string;
  date_of_birth?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
}

// Change Password Interface
export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

// Statistics Interface
export interface MembershipStats {
  totalMembers: number;
  activeMembers: number;
  inactiveMembers: number;
  suspendedMembers: number;
  totalRevenue: number;
  monthlyRevenue: number;
}

// Dashboard Data Interface
export interface DashboardData {
  member: Member;
  upcomingPayments: Payment[];
  recentInvoices: Invoice[];
  membershipTier: MembershipTier;
  benefits: Benefit[];
}

// =================================
// Kids Club Types
// =================================

// Booking Status Enum
export enum BookingStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  CANCELLED = 'cancelled',
  NO_SHOW = 'no_show',
  CHECKED_IN = 'checked_in',
  CHECKED_OUT = 'checked_out',
  COMPLETED = 'completed',
}

// Alert Severity Enum
export enum AlertSeverity {
  CRITICAL = 'critical',
  URGENT = 'urgent',
  INFO = 'info',
}

// Alert Type Enum
export enum AlertType {
  PICKUP_REMINDER = 'pickup_reminder',
  BEHAVIORAL_ISSUE = 'behavioral_issue',
  MEDICAL_EMERGENCY = 'medical_emergency',
  INCIDENT = 'incident',
  PARENT_REQUEST = 'parent_request',
  OTHER = 'other',
}

// Kids Club Capacity Interface
export interface KidsClubCapacity {
  id: string;
  date: string;
  session: 'AM' | 'PM' | 'FULL_DAY';
  max_capacity: number;
  current_bookings: number;
  available_spots: number;
  is_full: boolean;
  max_capacity_ages_3_5?: number;
  current_bookings_ages_3_5?: number;
  max_capacity_ages_6_12?: number;
  current_bookings_ages_6_12?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// Booking Type Enum
export enum BookingType {
  ADVANCE = 'advance',
  WALK_IN = 'walk-in',
  EMERGENCY = 'emergency',
}

// Kids Club Booking Interface
export interface KidsClubBooking {
  id: string;
  member_id: string;
  dependent_id: string;  // Changed to string UUID
  booking_date: string;
  start_time: string;
  end_time: string;
  session: 'AM' | 'PM' | 'FULL_DAY';
  status: BookingStatus;
  booking_type: BookingType;
  parent_location?: string;
  special_instructions?: string;
  cancelled_at?: string;
  cancelled_by_member_id?: string;
  cancellation_reason?: string;
  no_show_reason?: string;
  created_at: string;
  updated_at: string;
  // Relationships
  dependent?: FamilyMember;
  member?: Member;
  check_in?: KidsClubCheckIn;
}

// Kids Club Check-In Interface
export interface KidsClubCheckIn {
  id: string;
  booking_id: string;
  dependent_id: string;
  checked_in_at: string;
  checked_in_by_staff_id: string;
  checked_in_by_member_id?: string;
  checked_out_at?: string;
  checked_out_by_staff_id?: string;
  checked_out_by_member_id?: string;
  check_in_notes?: string;
  check_out_notes?: string;
  parent_location_at_checkin?: string;
  current_parent_location?: string;
  activities_participated?: string[];
  meals_consumed?: string[];
  behavior_notes?: string;
  incident_occurred?: boolean;
  incident_details?: string;
  created_at: string;
  updated_at: string;
  // Relationships
  booking?: KidsClubBooking;
  dependent?: {
    id: string;
    first_name: string;
    last_name: string;
    age?: number;
  };
  checked_in_by_member?: {
    id: string;
    first_name: string;
    last_name: string;
    phone?: string;
    relationship?: string;
  };
  checked_out_by_member?: {
    id: string;
    first_name: string;
    last_name: string;
    relationship?: string;
  };
}

// Emergency Alert Interface
export interface EmergencyAlert {
  id: string;
  booking_id: string;
  dependent_id: string;
  severity: AlertSeverity;
  alert_type: AlertType;
  message: string;
  channels_used: string[];
  sms_sent: boolean;
  sms_sent_at?: string;
  email_sent: boolean;
  email_sent_at?: string;
  push_sent: boolean;
  push_sent_at?: string;
  acknowledged: boolean;
  acknowledged_at?: string;
  acknowledged_by_member_id?: string;
  parent_eta?: string;
  resolved: boolean;
  resolved_at?: string;
  resolved_by_staff_id?: string;
  resolution_notes?: string;
  staff_initiated_by: string;
  created_at: string;
  updated_at: string;
  // Relationships
  booking?: KidsClubBooking;
  dependent?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  check_in?: {
    id: string;
    parent_location?: string;
    checked_in_by_member?: {
      id: string;
      first_name: string;
      last_name: string;
      phone?: string;
    };
  };
}

// Child Data Consent Interface
export interface ChildDataConsent {
  id: string;
  dependent_id: string;  // Changed to string UUID
  member_id: string;
  consent_type: string;
  consent_given: boolean;
  consent_date: string;
  withdrawn_at?: string;
  withdrawn_by_member_id?: string;
  last_reviewed_date?: string;
  expires_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// Kids Club Booking Form Data
export interface KidsClubBookingFormData {
  dependent_id: string;  // Changed to string UUID
  booking_date: string;
  start_time: string;
  end_time: string;
  session?: 'AM' | 'PM' | 'FULL_DAY';
  parent_location?: string;
  special_instructions?: string;
}

// Kids Club Check-In Form Data
export interface KidsClubCheckInFormData {
  booking_id: string;
  check_in_notes?: string;
  parent_location?: string;
  checked_in_by_member_id?: string;
}

// Kids Club Check-Out Form Data
export interface KidsClubCheckOutFormData {
  activities_participated?: string[];
  meals_consumed?: string[];
  behavior_notes?: string;
  incident_occurred?: boolean;
  incident_details?: string;
  check_out_notes?: string;
  checked_out_by_member_id?: string;
}

// Walk-In Form Data (for existing members)
export interface WalkInFormData {
  member_id: string;
  dependent_id: string;
  parent_location?: string;
  notes?: string;
}

// Member Lookup Result
export interface MemberLookupResult {
  member: {
    id: string;
    first_name: string;
    last_name: string;
    member_number: string;
    email?: string;
    phone?: string;
  };
  dependents: {
    id: string;
    first_name: string;
    last_name: string;
    date_of_birth?: string;
    age?: number;
    eligible: boolean;
  }[];
  eligible_count: number;
}

// Kids Club Settings
export interface TimeSlot {
  enabled: boolean;
  start_time: string;
  end_time: string;
  capacity: number;
}

export interface DaySchedule {
  closed: boolean;
  capacity: number;
  time_slots: {
    morning: TimeSlot;
    afternoon: TimeSlot;
    evening: TimeSlot;
  };
}

export interface KidsClubSettings {
  id: string;
  default_daily_capacity: number;
  operating_start_time: string;
  operating_end_time: string;
  weekly_schedule: {
    monday: DaySchedule;
    tuesday: DaySchedule;
    wednesday: DaySchedule;
    thursday: DaySchedule;
    friday: DaySchedule;
    saturday: DaySchedule;
    sunday: DaySchedule;
  };
  updated_at: string;
}

// Capacity Override
export interface CapacityOverride {
  id: string;
  date: string;
  max_capacity: number;
  is_closed: boolean;
  notes?: string;
  start_time?: string;  // HH:MM format for time-specific overrides
  end_time?: string;    // HH:MM format for time-specific overrides
  session?: 'AM' | 'PM' | 'FULL_DAY' | 'EVENING';
  required_staff_count?: number;
  special_event_name?: string;
}

// Booking Summary (for date view)
export interface BookingSummary {
  total_bookings: number;
  total_capacity: number;
  children_checked_in: number;
  children_checked_out: number;
  children_not_arrived: number;
  available_spots: number;
  is_closed: boolean;
}

// Emergency Alert Form Data
export interface EmergencyAlertFormData {
  booking_id: string;
  severity: AlertSeverity;
  alert_type: AlertType;
  message: string;
  channels?: string[];
}

// Capacity Query Params
export interface CapacityQueryParams {
  date?: string;
  start_date?: string;
  end_date?: string;
  min_spots?: number;
}

// Kids Club API Response Interfaces
export interface KidsClubBookingResponse {
  success: boolean;
  data: KidsClubBooking;
  message?: string;
}

export interface KidsClubBookingsResponse {
  success: boolean;
  data: KidsClubBooking[];
  message?: string;
}

export interface KidsClubCapacityResponse {
  success: boolean;
  data: KidsClubCapacity[];
  message?: string;
}

export interface KidsClubCheckInResponse {
  success: boolean;
  data: KidsClubCheckIn;
  message?: string;
}

export interface EmergencyAlertResponse {
  success: boolean;
  data: EmergencyAlert;
  message?: string;
}

// =================================
// Court Booking Types
// =================================

// Court Type Enum
export enum CourtType {
  TENNIS = 'tennis',
  PICKLEBALL = 'pickleball',
}

// Court Surface Enum
export enum CourtSurface {
  HARD = 'hard',
  CLAY = 'clay',
  GRASS = 'grass',
}

// Court Status Enum
export enum CourtStatus {
  AVAILABLE = 'available',
  MAINTENANCE = 'maintenance',
  CLOSED = 'closed',
}

// Reservation Status Enum
export enum ReservationStatus {
  CONFIRMED = 'confirmed',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
  NO_SHOW = 'no_show',
}

// Reservation Duration Enum
export enum ReservationDuration {
  THIRTY_MINUTES = 30,
  ONE_HOUR = 60,
  NINETY_MINUTES = 90,
}

// Reservation Type Enum
export enum ReservationType {
  SINGLES = 'singles',
  DOUBLES = 'doubles',
  BALL_MACHINE = 'ball_machine',
}

// Player Interface for reservations
export interface ReservationPlayer {
  id: string;
  type: 'member' | 'non_member';
  name: string;
}

// Court Interface
export interface Court {
  id: string;
  court_number: number;
  name: string;
  court_type: CourtType;
  surface: CourtSurface;
  status: CourtStatus;
  is_active: boolean;
  description?: string;
  operating_hours?: CourtOperatingHours[];
  created_at: string;
  updated_at: string;
}

// Court Operating Hours Interface
export interface CourtOperatingHours {
  id: string;
  court_id?: string;
  day_of_week: number;
  day_name: string;
  open_time: string;
  close_time: string;
  is_closed: boolean;
  effective_date?: string;
  created_at: string;
  updated_at: string;
}

// Court Reservation Interface
export interface CourtReservation {
  id: string;
  court_id: string;
  member_id: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  status: ReservationStatus;
  reservation_type: ReservationType;
  players: ReservationPlayer[];
  guest_count: number;
  guest_names?: string;
  notes?: string;
  created_by_id?: string;
  cancelled_at?: string;
  cancelled_by_id?: string;
  cancellation_reason?: string;
  created_at: string;
  updated_at: string;
  // Relationships
  court?: {
    id: string;
    court_number: number;
    name: string;
    court_type: string;
    surface: string;
  };
  member?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    member_number: string;
  };
}

// Court Reservation Form Data
export interface CourtReservationFormData {
  court_id: string;
  start_time: string;
  duration_minutes: number;
  reservation_type: ReservationType;
  players?: ReservationPlayer[];
  guest_count?: number;
  guest_names?: string;
  notes?: string;
}

// Court Schedule Response
export interface CourtSchedule {
  court: Court;
  date: string;
  operating_hours?: CourtOperatingHours;
  reservations: CourtReservation[];
  reservation_count: number;
}

// Court Availability Check Response
export interface CourtAvailability {
  available: boolean;
  court_id: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  reason?: string;
}

// Court API Response Interfaces
export interface CourtResponse {
  success: boolean;
  data: Court;
  message?: string;
}

export interface CourtsResponse {
  success: boolean;
  data: Court[];
  count: number;
  message?: string;
}

export interface CourtReservationResponse {
  success: boolean;
  data: CourtReservation;
  message?: string;
}

export interface CourtReservationsResponse {
  success: boolean;
  data: CourtReservation[];
  count: number;
  message?: string;
}

export interface CourtScheduleResponse {
  success: boolean;
  data: CourtSchedule;
  message?: string;
}

export interface CourtAvailabilityResponse {
  success: boolean;
  data: CourtAvailability;
  message?: string;
}

export interface CourtOperatingHoursResponse {
  success: boolean;
  data: CourtOperatingHours[];
  count: number;
  message?: string;
}

// =================================
// Pro Booking Types
// =================================

// Pro Booking Status Enum
export enum ProBookingStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
  NO_SHOW = 'no_show',
}

// Pro Interface (Staff member who is a tennis/pickleball pro)
export interface Pro {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  profile_photo_url?: string;
  departments: string[];
  bio?: string;
  specialties?: string[];
  session_types: ProSessionType[];
  availability?: ProAvailability[];
}

// Pro Availability Interface
export interface ProAvailability {
  id: string;
  staff_id: string;
  day_of_week: number; // 0-6 (Monday=0)
  start_time: string; // HH:MM format
  end_time: string;   // HH:MM format
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// Pro Session Type Interface
export interface ProSessionType {
  id: string;
  staff_id: string;
  name: string;
  description?: string;
  duration_minutes: number;
  max_participants: number;
  price_member: number;
  price_non_member?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Pro Booking Participant Interface
export interface ProBookingParticipant {
  id: string;
  type: 'member' | 'non_member';
  name: string;
}

// Pro Booking Interface
export interface ProBooking {
  id: string;
  pro_id: string;
  session_type_id: string;
  court_id?: string;
  booked_by_id: string;
  start_time: string;
  end_time: string;
  status: ProBookingStatus;
  participants: ProBookingParticipant[];
  total_price: number;
  notes?: string;
  cancelled_at?: string;
  cancelled_by_id?: string;
  cancellation_reason?: string;
  created_at: string;
  updated_at: string;
  // Relationships
  pro?: {
    id: string;
    first_name: string;
    last_name: string;
    profile_photo_url?: string;
  };
  session_type?: ProSessionType;
  court?: {
    id: string;
    court_number: number;
    name: string;
  };
  booked_by?: {
    id: string;
    first_name: string;
    last_name: string;
    member_number?: string;
  };
}

// Pro Booking Form Data
export interface ProBookingFormData {
  pro_id: string;
  session_type_id: string;
  start_time: string;
  court_id?: string;
  participants?: ProBookingParticipant[];
  notes?: string;
}

// Available Time Slot Interface
export interface AvailableTimeSlot {
  start_time: string;
  end_time: string;
  available: boolean;
}

// Pro Availability Schedule Response
export interface ProAvailabilitySchedule {
  pro_id: string;
  date: string;
  recurring_availability: ProAvailability[];
  available_slots: AvailableTimeSlot[];
  existing_bookings: {
    start_time: string;
    end_time: string;
  }[];
}

// Pro Booking API Response Interfaces
export interface ProResponse {
  success: boolean;
  data: Pro;
  message?: string;
}

export interface ProsResponse {
  success: boolean;
  data: Pro[];
  count: number;
  message?: string;
}

export interface ProBookingResponse {
  success: boolean;
  data: ProBooking;
  message?: string;
}

export interface ProBookingsResponse {
  success: boolean;
  data: ProBooking[];
  count: number;
  message?: string;
}

export interface ProAvailabilityResponse {
  success: boolean;
  data: ProAvailabilitySchedule;
  message?: string;
}

export interface ProSessionTypesResponse {
  success: boolean;
  data: ProSessionType[];
  count: number;
  message?: string;
}

// =================================
// Program Types
// =================================

// Program Status Enum
export enum ProgramStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
}

// Program Type Enum
export enum ProgramType {
  TENNIS = 'tennis',
  PICKLEBALL = 'pickleball',
  FITNESS = 'fitness',
  SWIMMING = 'swimming',
  GOLF = 'golf',
  OTHER = 'other',
}

// Enrollment Status Enum
export enum EnrollmentStatus {
  REGISTERED = 'registered',
  WAITLISTED = 'waitlisted',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
}

// Program Recurrence Pattern Interface
export interface ProgramRecurrencePattern {
  days: number[]; // Array of day indices (0-6, Monday=0)
  time: string;   // HH:MM format
  duration: number; // Duration in minutes
}

// Program Interface
export interface Program {
  id: string;
  name: string;
  description?: string;
  program_type: ProgramType | string;
  instructor_id: string;
  court_id?: string;
  price_member: number;
  price_non_member?: number;
  max_participants: number;
  min_participants?: number;
  start_date: string;
  end_date: string;
  recurrence_pattern?: ProgramRecurrencePattern;
  status: ProgramStatus;
  registration_deadline?: string;
  is_active: boolean;
  created_by_id: string;
  created_at: string;
  updated_at: string;
  // Relationships
  instructor?: {
    id: string;
    first_name: string;
    last_name: string;
    profile_photo_url?: string;
  };
  court?: {
    id: string;
    court_number: number;
    name: string;
  };
  sessions?: ProgramSession[];
  enrollment_count?: number;
  spots_available?: number;
}

// Program Session Interface
export interface ProgramSession {
  id: string;
  program_id: string;
  session_date: string;
  start_time: string;
  end_time: string;
  is_cancelled: boolean;
  cancellation_reason?: string;
  created_at: string;
  updated_at: string;
}

// Program Enrollment Interface
export interface ProgramEnrollment {
  id: string;
  program_id: string;
  member_id?: string;
  non_member_id?: string;
  enrolled_by_id: string;
  enrolled_by_type: 'member' | 'staff';
  status: EnrollmentStatus;
  amount_due: number;
  amount_paid: number;
  payment_status: 'pending' | 'paid' | 'refunded' | 'partial';
  notes?: string;
  enrolled_at: string;
  cancelled_at?: string;
  cancelled_by_id?: string;
  created_at: string;
  updated_at: string;
  // Relationships
  program?: Program;
  member?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    member_number?: string;
  };
  non_member?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  dependent_id?: string;
  non_member_dependent_id?: number;
  // Resolved enrollee from the backend (to_dict include_enrollee) — ONE shape for a
  // member, non-member, member dependent, or non-member dependent. Prefer this over the
  // legacy member/non_member objects above (which the API no longer populates).
  enrollee?: {
    id: string | number;
    type: 'member' | 'non_member' | 'dependent' | 'non_member_dependent';
    is_member?: boolean;
    first_name: string;
    last_name: string;
    email?: string;
    member_number?: string;
    date_of_birth?: string | null;
  };
}

// Program Form Data
export interface ProgramFormData {
  name: string;
  description?: string;
  program_type: ProgramType | string;
  instructor_id: string;
  court_id?: string;
  price_member: number;
  price_non_member?: number;
  max_participants: number;
  min_participants?: number;
  start_date: string;
  end_date: string;
  recurrence_pattern?: ProgramRecurrencePattern;
  registration_deadline?: string;
}

// Program Enrollment Form Data
export interface ProgramEnrollmentFormData {
  member_id?: string;
  non_member_id?: string;
}

// Program API Response Interfaces
export interface ProgramResponse {
  success: boolean;
  data: Program;
  message?: string;
}

export interface ProgramsResponse {
  success: boolean;
  data: Program[];
  count: number;
  message?: string;
}

export interface ProgramEnrollmentResponse {
  success: boolean;
  data: ProgramEnrollment;
  message?: string;
}

export interface ProgramEnrollmentsResponse {
  success: boolean;
  data: ProgramEnrollment[];
  count: number;
  message?: string;
}

export interface ProgramSessionsResponse {
  success: boolean;
  data: ProgramSession[];
  count: number;
  message?: string;
}

// =================================
// Non-Member Family Types
// =================================

// Non-Member Relationship Enum
export enum NonMemberRelationship {
  CHILD = 'child',
  SPOUSE = 'spouse',
  PARTNER = 'partner',
  PARENT = 'parent',
  SIBLING = 'sibling',
  OTHER = 'other',
}

// Non-Member Family Interface
export interface NonMemberFamily {
  id: number;
  non_member_id: string;
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  age?: number;
  relationship_type: NonMemberRelationship | string;
  // Account number, inherited from the primary non-member with a -DP suffix
  // (e.g. "NM00012-DP01"). Auto-assigned; staff-only to override.
  member_sub_number?: string | null;
  email?: string;
  phone?: string;
  kids_club_eligible: boolean;
  lessons_eligible: boolean;
  allergies?: string;
  medical_conditions?: string;
  dietary_restrictions?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  photo_consent: boolean;
  authorized_pickup_persons?: { name: string; phone: string; relationship: string }[];
  notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  converted_to_family_member_id?: number;
}

// Non-Member Family Form Data
export interface NonMemberFamilyFormData {
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  relationship_type: NonMemberRelationship | string;
  // Staff-only override of the account number (auto-assigned on create).
  member_sub_number?: string | null;
  email?: string;
  phone?: string;
  kids_club_eligible?: boolean;
  lessons_eligible?: boolean;
  allergies?: string;
  medical_conditions?: string;
  dietary_restrictions?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  photo_consent?: boolean;
  authorized_pickup_persons?: { name: string; phone: string; relationship: string }[];
  notes?: string;
}

// =================================
// Conversion Application Types
// =================================

// Conversion Status Enum
export enum ConversionStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  UNDER_REVIEW = 'under_review',
  ADDITIONAL_INFO = 'additional_info',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  WAITLISTED = 'waitlisted',
  PAYMENT_PENDING = 'payment_pending',
  CONVERTED = 'converted',
  WITHDRAWN = 'withdrawn',
}

// Conversion Application Interface
export interface ConversionApplication {
  id: string;
  applicant_member_id: string;
  application_number: string;
  status: ConversionStatus | string;
  requested_tier_id?: number;
  requested_member_type: string;
  joining_household_member_id?: string;
  // Sponsorship
  sponsor1_member_id?: string;
  sponsor2_member_id?: string;
  sponsor1_approved?: boolean;
  sponsor2_approved?: boolean;
  sponsor1_approved_at?: string;
  sponsor2_approved_at?: string;
  // Application data
  application_data?: Record<string, unknown>;
  family_members_to_convert?: number[];
  // Review
  submitted_at?: string;
  reviewed_by_staff_id?: string;
  reviewed_at?: string;
  review_notes?: string;
  // Committee
  committee_review_date?: string;
  committee_decision?: string;
  committee_notes?: string;
  // Decision
  decision_date?: string;
  decision_by_staff_id?: string;
  rejection_reason?: string;
  // Waitlist
  waitlist_position?: number;
  waitlist_added_date?: string;
  // Payment
  initiation_fee_amount?: number;
  first_dues_amount?: number;
  payment_due_date?: string;
  payment_received_date?: string;
  payment_id?: string;
  // Conversion
  converted_at?: string;
  new_member_number?: string;
  // Timestamps
  created_at: string;
  updated_at: string;
  // Relationships
  applicant?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    member_type: string;
  };
  requested_tier?: {
    id: number;
    tier_name: string;
    tier_code?: string;
  };
  sponsor1?: {
    id: string;
    first_name: string;
    last_name: string;
    member_number?: string;
  };
  sponsor2?: {
    id: string;
    first_name: string;
    last_name: string;
    member_number?: string;
  };
}

// Conversion Application Form Data
export interface ConversionApplicationFormData {
  requested_tier_id: number;
  requested_member_type?: string;
  joining_household_member_id?: string;
  sponsor1_member_id?: string;
  sponsor2_member_id?: string;
  application_data?: Record<string, unknown>;
  family_members_to_convert?: number[];
}

// Conversion Preview Interface
export interface ConversionPreview {
  success: boolean;
  preview: boolean;
  application: {
    id: string;
    application_number: string;
    status: string;
    can_convert: boolean;
  };
  applicant: {
    id: string;
    current_type: string;
    will_become_type: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  preview_member_number: string;
  requested_tier_id?: number;
  family_members_to_convert: {
    non_member_family_id: number;
    name: string;
    relationship: string;
    preview_sub_number: string;
    already_converted: boolean;
  }[];
  family_member_count: number;
  payment_info: {
    initiation_fee?: number;
    first_dues?: number;
    payment_due_date?: string;
    payment_received: boolean;
  };
}

// =================================
// Admin Analytics Types
// =================================

// Non-Member Analytics Interface
export interface NonMemberAnalytics {
  total_non_members: number;
  active_non_members: number;
  inactive_non_members: number;
  blocked_non_members: number;
  prospects: number;
  by_acquisition_source: Record<string, number>;
  by_interest_level: Record<string, number>;
  recent_registrations_30d: number;
  generated_at: string;
}

// Conversion Funnel Interface
export interface ConversionFunnel {
  date_range: {
    start: string;
    end: string;
  };
  funnel: {
    non_members: number;
    expressed_interest: number;
    applications_submitted: number;
    under_review: number;
    approved_pending_payment: number;
    waitlisted: number;
    rejected: number;
    converted: number;
  };
  rates: {
    submit_rate_percent: number;
    approval_rate_percent: number;
    conversion_rate_percent: number;
  };
  generated_at: string;
}

// Guest Pass Analytics Interface
export interface GuestPassAnalytics {
  date_range: {
    start: string;
    end: string;
  };
  pass_counts: {
    total: number;
    issued: number;
    active: number;
    completed: number;
    cancelled: number;
    no_show: number;
    expired: number;
  };
  rates: {
    usage_rate_percent: number;
    no_show_rate_percent: number;
  };
  guest_visit_usage: {
    total_visits_ytd: number;
    average_per_non_member: number;
    at_limit: number;
    approaching_limit: number;
  };
  popular_facilities: Record<string, number>;
  generated_at: string;
}

// Admin Dashboard Interface
export interface AdminDashboard {
  non_member_summary: NonMemberAnalytics;
  conversion_funnel: ConversionFunnel['funnel'];
  guest_pass_summary: GuestPassAnalytics['pass_counts'];
  today: {
    guest_passes_today: number;
    pending_checkin: number;
    currently_active: number;
  };
  action_items: {
    pending_applications: number;
    waitlist_count: number;
  };
  generated_at: string;
}

// Activity Report Interface
export interface ActivityReport {
  period: {
    days: number;
    start: string;
    end: string;
  };
  totals: {
    new_registrations: number;
    guest_passes: number;
    applications: number;
    conversions: number;
  };
  daily: Record<string, {
    registrations: number;
    guest_passes_issued: number;
    guest_check_ins: number;
    applications_submitted: number;
    conversions: number;
  }>;
  generated_at: string;
}

// Upcoming Anniversary Reset Interface
export interface UpcomingAnniversaryReset {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  acquisition_date: string;
  anniversary_date: string;
  days_until: number;
  current_guest_visits: number;
  annual_limit: number;
}

// Guest Pass Form Data
export interface GuestPassFormData {
  guest_member_id?: string;
  sponsor_member_id?: string;
  visit_date: string;
  facilities_allowed?: string[];
  guest_fee_amount?: number;
  fee_billed_to?: string;
  notes?: string;
  // For creating new guest
  guest_first_name?: string;
  guest_last_name?: string;
  guest_email?: string;
  guest_phone?: string;
}

// =================================
// Non-Member API Response Types
// =================================

export interface NonMemberResponse {
  success: boolean;
  data: NonMember;
  message?: string;
}

export interface NonMembersResponse {
  success: boolean;
  data: NonMember[];
  count: number;
  message?: string;
}

export interface NonMemberFamilyResponse {
  success: boolean;
  data: NonMemberFamily;
  message?: string;
}

export interface NonMemberFamiliesResponse {
  success: boolean;
  data: NonMemberFamily[];
  count: number;
  message?: string;
}

export interface GuestPassResponse {
  success: boolean;
  data: GuestPass;
  message?: string;
}

export interface GuestPassesResponse {
  success: boolean;
  data: GuestPass[];
  count: number;
  message?: string;
}

export interface ConversionApplicationResponse {
  success: boolean;
  data: ConversionApplication;
  message?: string;
}

export interface ConversionApplicationsResponse {
  success: boolean;
  data: ConversionApplication[];
  count: number;
  message?: string;
}

export interface AdminDashboardResponse {
  success: boolean;
  data: AdminDashboard;
  message?: string;
}

export interface NonMemberAnalyticsResponse {
  success: boolean;
  data: NonMemberAnalytics;
  message?: string;
}

export interface ConversionFunnelResponse {
  success: boolean;
  data: ConversionFunnel;
  message?: string;
}

export interface GuestPassAnalyticsResponse {
  success: boolean;
  data: GuestPassAnalytics;
  message?: string;
}

export interface ActivityReportResponse {
  success: boolean;
  data: ActivityReport;
  message?: string;
}

export interface UpcomingAnniversaryResetsResponse {
  success: boolean;
  data: {
    upcoming_resets: UpcomingAnniversaryReset[];
    count: number;
    days_ahead: number;
    generated_at: string;
  };
  message?: string;
}
